import ClearIcon from "@mui/icons-material/Clear";
import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";
import { SuggestedChip, SuggestedChipProps } from "components/ArtificialIntelligence/SuggestedChip";
import { SuggestedIcon } from "components/ArtificialIntelligence/SuggestedIcon";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { isNil } from "lodash-es";
import React, { ReactNode, useCallback, useMemo } from "react";
import { Controller, FieldValues, RegisterOptions, UseControllerProps } from "react-hook-form";

import { DisplayOnlyField } from "./DisplayOnlyField";
import { ErrorDisplay } from "./ErrorDisplay";
import { TextWithTooltip } from "./TextWithTooltip";

export type SelectItem = {
  value: any;
  display: string;
  disabled?: boolean;
};

export const FormSelect = <T extends FieldValues>({
  name,
  label,
  tooltip,
  disabled,
  control,
  selectProps,
  selectItems,
  helperText,
  rules,
  variant,
  sx,
  selectSx,
  hideErrors,
  clearable = true,
  suggestionProps,
}: {
  label: string;
  tooltip?: ReactNode;
  disabled?: boolean;
  selectProps?: SelectProps;
  selectItems: SelectItem[];
  helperText?: string | React.JSX.Element;
  rules?: RegisterOptions;
  variant?: "standard" | "outlined" | "filled";
  sx?: SxProps<Theme>;
  selectSx?: SxProps<Theme>;
  hideErrors?: boolean;
  clearable?: boolean;
  suggestionProps?: SuggestedChipProps & { showFullSuggestionChip?: boolean };
} & UseControllerProps<T>) => {
  const inputLabelId = useMemo(() => `${name}label`, [name]);
  const theme = useTheme();
  const labelWithTooltip = useMemo(
    () => (tooltip ? <TextWithTooltip text={label} tooltip={tooltip} /> : label),
    [label, tooltip]
  );

  const handleOnMouseDownForClearButton = useCallback((event: any) => {
    event.stopPropagation();
  }, []);

  const handleClear = useCallback((event: any, field: any) => {
    event.stopPropagation();
    field.onChange(null);
  }, []);

  const formContext = useValidatingFormContext();
  if (formContext?.metadata?.isDisplayOnly) {
    const value = formContext?.getValues(name);
    const values = Array.isArray(value)
      ? value.filter((v: string) => v != null)
      : value != null
        ? [value]
        : [];
    const text = selectItems.length
      ? values
          .map((v: string) => selectItems.find((item) => item.value === v)?.display ?? v)
          .join(", ")
      : (value?.toString() ?? "");
    return <DisplayOnlyField text={text} label={labelWithTooltip ?? ""} />;
  }

  const defaultValue = selectProps?.multiple ? [] : "";

  return (
    <FormControl fullWidth sx={sx} required={!!rules?.required}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => {
          let value: string | string[] = field.value;
          if (selectProps?.multiple) {
            value = Array.isArray(value) ? value : [];
          }
          if (isNil(value)) {
            value = defaultValue;
          }

          return (
            <>
              <InputLabel
                error={!!error}
                sx={{
                  color: disabled ? theme.palette.action.disabled : theme.palette.text.secondary,
                }}
                id={inputLabelId}
              >
                {labelWithTooltip}
              </InputLabel>
              <Select
                {...selectProps}
                {...field}
                value={value}
                name={name}
                label={labelWithTooltip}
                error={!!error}
                labelId={inputLabelId}
                aria-labelledby={inputLabelId}
                disabled={disabled}
                variant={variant}
                sx={selectSx}
                startAdornment={
                  suggestionProps &&
                  !suggestionProps.showFullSuggestionChip && <SuggestedIcon {...suggestionProps} />
                }
                renderValue={() => {
                  const selectedValues = Array.isArray(value) ? value : [value];
                  const display = selectProps?.multiple
                    ? selectedValues
                        .map(
                          (v) => selectItems.find((opt) => opt.value === v)?.display ?? String(v)
                        )
                        .join(", ")
                    : (selectItems.find((opt) => opt.value === value)?.display ?? "");
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        minWidth: 0,
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {display}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          flexShrink: 0,
                        }}
                      >
                        {suggestionProps?.showFullSuggestionChip && (
                          <SuggestedChip
                            label="Suggested"
                            {...suggestionProps}
                            hideRejectButton={clearable} // Clearable and reject both are an X, so they cannot both be shown
                          />
                        )}
                        {clearable && (
                          <IconButton
                            size="small"
                            onMouseDown={handleOnMouseDownForClearButton}
                            onClick={(event) => handleClear(event, field)}
                            sx={{ p: 0 }}
                            disabled={disabled}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  );
                }}
              >
                {selectItems?.map((item: SelectItem) => {
                  const isSelected = selectProps?.multiple
                    ? Array.isArray(value) && value.includes(item.value)
                    : value === item.value;
                  return (
                    <MenuItem key={item.value} value={item.value} disabled={item.disabled}>
                      {selectProps?.multiple ? (
                        <>
                          <Checkbox checked={isSelected} sx={{ py: 0 }} />
                          <ListItemText primary={item.display} />
                        </>
                      ) : (
                        item.display
                      )}
                    </MenuItem>
                  );
                })}
              </Select>
              <ErrorDisplay error={error} helperText={helperText} hideErrors={hideErrors} />
            </>
          );
        }}
      />
    </FormControl>
  );
};
